import {
  Button,
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  Tooltip,
  mergeClasses,
} from "@fluentui/react-components";
import { MoreVertical16Regular, OpenRegular } from "@fluentui/react-icons";
import { addDiagnosticCreated, addObjectiveCreated } from "api";
import {
  addObjectiveToDiagnostic,
  createDiagnostic,
  updateDiagnostic,
} from "apps/diagnostics/api";
import { IDiagnostic } from "apps/diagnostics/models/IDiagnostic";
import { IObjective } from "apps/diagnostics/models/IObjective";
import { ConstantValues } from "common/constants";
import { DocumentCopyIcon } from "icons/DocumentCopyIcon";
import { EditIcon } from "icons/EditIcon";
import { TrashIcon } from "icons/TrashIcon";
import { useOverflowTooltip } from "hooks/useOverflowTooltip";
import { MouseEvent, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  useSharedCardDetailStyles,
  useSharedCardStyles,
  useSharedMenuStyles,
} from "theme/sharedStyles";
import { addDiagnostic } from "../../../../../redux/diagnosticsSlice";
import { showToast } from "../../../../../redux/toastActions";
import { CopyDiagnosticDialog } from "../../copyDiagnosticDialog/copyDiagnosticDialog";
import { EditDiagnostic } from "../editDialog/EditDiagnostic";
import { buildObjectiveCopyBody } from "./DiagnosticCard.logic";
import { IDiagnosticCardProps } from "./diagnosticCard.types";

const MAX_COLLABORATORS_DISPLAY = 3;

export const DiagnosticCard = (props: IDiagnosticCardProps): JSX.Element => {
  const cardDetailStyles = useSharedCardDetailStyles();
  const sharedCardStyles = useSharedCardStyles();
  const sharedMenuStyles = useSharedMenuStyles();
  const navigate = useNavigate();
  const [isEditPopupOpen, setIsEditPopupOpen] = useState<boolean>(false);
  const [isCopying, setIsCopying] = useState<boolean>(false);

  const titleOverflow = useOverflowTooltip();
  const clientOverflow = useOverflowTooltip();
  const descOverflow = useOverflowTooltip();

  const dispatch = useDispatch();

  const handleEdit = () => {
    setIsEditPopupOpen(true);
  };

  const updatedDate: string = new Date(
    Number(props.diagnostic.updatedAt || props.diagnostic.createdAt) * 1000,
  ).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

  const handleManageDiagnosticClick = () => {
    navigate(`/diagnostic/${props.diagnostic.id}`);
  };

  const handleOpenButtonClick = (e: MouseEvent) => {
    e.stopPropagation();
    handleManageDiagnosticClick();
  };

  const handleMenuClick = (e: MouseEvent) => {
    e.stopPropagation();
  };

  const handleCopy = async () => {
    if (isCopying) return;
    setIsCopying(true);
    try {
      const created = await createDiagnostic({
        name: `${props.diagnostic.name} - Copy`,
        description: props.diagnostic.description,
        client: props.diagnostic.client,
        background: props.diagnostic.background,
        collaborators: props.diagnostic.collaborators,
        beginningMessage: props.diagnostic.beginningMessage,
        endingMessage: props.diagnostic.endingMessage,
        headerColor: props.diagnostic.headerColor,
        headerLogoUrl: props.diagnostic.headerLogoUrl,
        accentColor: props.diagnostic.accentColor,
        customBrandingEnabled: props.diagnostic.customBrandingEnabled,
        demographicsQuestions: props.diagnostic.demographicsQuestions,
        demographicsPlacement: props.diagnostic.demographicsPlacement,
        demographicsQuestionOverrides:
          props.diagnostic.demographicsQuestionOverrides,
      });

      await addDiagnosticCreated({
        diagnosticId: created._id?.toString() || ConstantValues.EMPTY_STRING,
      });

      const createdWithFiles: IDiagnostic = await updateDiagnostic({
        id: created.id,
        diagnostic: { ...created, attachments: props.diagnostic.attachments },
      });

      const sourceObjectives = props.diagnostic.objectives
        ? [...props.diagnostic.objectives]
        : [];

      const objectives: IObjective[] = [];
      let failedObjectiveCount = 0;
      for (const objective of sourceObjectives) {
        try {
          const body = buildObjectiveCopyBody(objective);

          const saved: IObjective = await addObjectiveToDiagnostic({
            id: created.id,
            body,
          });

          await addObjectiveCreated({
            objectiveId: saved._id?.toString() || ConstantValues.EMPTY_STRING,
          });

          objectives.push(saved);
        } catch (error) {
          failedObjectiveCount += 1;
          console.error(
            "Failed to copy objective:",
            objective.questionNumber,
            error,
          );
        }
      }

      createdWithFiles.objectives = objectives;
      if (createdWithFiles.id) {
        dispatch(addDiagnostic(createdWithFiles));
      }
      if (failedObjectiveCount > 0) {
        dispatch(
          showToast(
            `Diagnostic copied, but ${failedObjectiveCount} of ${sourceObjectives.length} objective(s) failed to copy`,
            "warning",
          ),
        );
      }
    } catch (error) {
      console.error("Failed to copy diagnostic:", error);
      dispatch(showToast("Failed to copy diagnostic", "error"));
    } finally {
      setIsCopying(false);
    }
  };

  const collaborators = props.diagnostic.collaborators ?? [];
  const displayedCollaborators = collaborators.slice(
    0,
    MAX_COLLABORATORS_DISPLAY,
  );
  const remainingCount = collaborators.length - MAX_COLLABORATORS_DISPLAY;
  const collaboratorsText = displayedCollaborators
    .map((c) => c.displayName)
    .join(", ");
  const fullCollaboratorsText = collaborators
    .map((c) => c.displayName)
    .join(", ");

  return (
    <div
      className={mergeClasses(
        sharedCardStyles.card,
        sharedCardStyles.cardInteractive,
        cardDetailStyles.cardOverride,
      )}
      onClick={handleManageDiagnosticClick}
    >
      <div className={cardDetailStyles.headerRow}>
        <div className={cardDetailStyles.fixedTitleSection}>
          <Tooltip
            content={props.diagnostic.name || "-"}
            relationship="description"
            appearance="inverted"
            positioning="above-start"
            withArrow
            visible={titleOverflow.visible}
          >
            <div
              ref={titleOverflow.ref}
              onMouseEnter={titleOverflow.onMouseEnter}
              onMouseLeave={titleOverflow.onMouseLeave}
              className={cardDetailStyles.title}
            >
              {props.diagnostic.name || "-"}
            </div>
          </Tooltip>
          <Tooltip
            content={props.diagnostic.client || "-"}
            relationship="description"
            appearance="inverted"
            positioning="above-start"
            withArrow
            visible={props.diagnostic.client ? clientOverflow.visible : false}
          >
            <div
              ref={clientOverflow.ref}
              onMouseEnter={clientOverflow.onMouseEnter}
              onMouseLeave={clientOverflow.onMouseLeave}
              className={mergeClasses(
                cardDetailStyles.subtitle,
                !props.diagnostic.client && cardDetailStyles.emptyValue,
              )}
              {...(!props.diagnostic.client && {
                "aria-label": "Not available",
              })}
            >
              {props.diagnostic.client || "-"}
            </div>
          </Tooltip>
        </div>
        <div onClick={handleMenuClick}>
          <Menu>
            <MenuTrigger disableButtonEnhancement>
              <Button
                icon={<MoreVertical16Regular />}
                appearance="subtle"
                size="small"
                aria-label="Diagnostic actions"
              />
            </MenuTrigger>
            <MenuPopover className={sharedMenuStyles.surface}>
              <MenuList>
                <MenuItem
                  icon={
                    <span className={sharedMenuStyles.itemIcon}>
                      <EditIcon size={16} />
                    </span>
                  }
                  onClick={handleEdit}
                >
                  Edit
                </MenuItem>
                <MenuItem
                  icon={
                    <span className={sharedMenuStyles.itemIcon}>
                      <DocumentCopyIcon size={16} />
                    </span>
                  }
                  onClick={handleCopy}
                  disabled={isCopying}
                >
                  Copy
                </MenuItem>
                {(props.isOwner || props.isDiagnosticsAdmin) && (
                  <MenuItem
                    icon={
                      <span className={sharedMenuStyles.itemIconDanger}>
                        <TrashIcon size={16} />
                      </span>
                    }
                    className={sharedMenuStyles.itemDanger}
                    onClick={() => props.onDelete(props.diagnostic)}
                  >
                    Delete
                  </MenuItem>
                )}
              </MenuList>
            </MenuPopover>
          </Menu>
        </div>
      </div>

      <Tooltip
        relationship="description"
        content={props.diagnostic.description || "-"}
        appearance="inverted"
        positioning="above-start"
        withArrow
        visible={props.diagnostic.description ? descOverflow.visible : false}
      >
        <div
          ref={descOverflow.ref}
          onMouseEnter={descOverflow.onMouseEnter}
          onMouseLeave={descOverflow.onMouseLeave}
          className={mergeClasses(
            cardDetailStyles.fixedDescription,
            !props.diagnostic.description && cardDetailStyles.emptyValue,
          )}
        >
          {props.diagnostic.description || "-"}
        </div>
      </Tooltip>

      <div className={cardDetailStyles.metadataSection}>
        <div className={cardDetailStyles.metadataRow}>
          <span className={cardDetailStyles.metadataLabel}>Owner</span>
          <span
            className={mergeClasses(
              cardDetailStyles.metadataValue,
              !props.diagnostic.user?.displayName &&
                cardDetailStyles.emptyValue,
            )}
            {...(!props.diagnostic.user?.displayName && {
              "aria-label": "Not available",
            })}
          >
            {props.diagnostic.user?.displayName || "-"}
          </span>
        </div>

        <div className={cardDetailStyles.metadataRow}>
          <span className={cardDetailStyles.metadataLabel}>Collaborators</span>
          {collaborators.length > 0 ? (
            <Tooltip relationship="label" content={fullCollaboratorsText}>
              <span className={cardDetailStyles.metadataValue}>
                {collaboratorsText}
                {remainingCount > 0 && ` +${remainingCount}`}
              </span>
            </Tooltip>
          ) : (
            <span
              className={mergeClasses(
                cardDetailStyles.metadataValue,
                cardDetailStyles.emptyValue,
              )}
              aria-label="Not available"
            >
              -
            </span>
          )}
        </div>

        <div className={cardDetailStyles.metadataRow}>
          <span className={cardDetailStyles.metadataLabel}>Updated</span>
          <span className={cardDetailStyles.metadataValue}>
            {updatedDate || "-"}
          </span>
        </div>
      </div>

      <div className={cardDetailStyles.footer}>
        <Tooltip content="Open diagnostic" relationship="description">
          <Button
            size="small"
            appearance="subtle"
            icon={<OpenRegular />}
            onClick={handleOpenButtonClick}
            aria-label={`Open ${props.diagnostic.name}`}
          />
        </Tooltip>
      </div>

      {isEditPopupOpen && (
        <div onClick={handleMenuClick}>
          <EditDiagnostic
            isOpen={isEditPopupOpen}
            diagnostic={props.diagnostic}
            isDiagnosticsAdmin={props.isDiagnosticsAdmin}
            onDismiss={() => setIsEditPopupOpen(false)}
          />
        </div>
      )}

      {isCopying && (
        <div onClick={handleMenuClick}>
          <CopyDiagnosticDialog
            open={isCopying}
            diagnosticName={
              props.diagnostic.name || ConstantValues.EMPTY_STRING
            }
          />
        </div>
      )}
    </div>
  );
};
